import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './ActecoSelect.css';

// Render options lazily: paint this many at first, then more as the user scrolls.
const PAGE = 60;
const labelOf = (o) => (o ? `${o.code} · ${o.activity}` : '');

/**
 * Searchable ACTECO activity selector. Catalog-only combobox (no free text):
 * the value is always a catalog option or null. Self-contained ARIA combobox
 * (no MUI): input + filtered listbox portalled to <body> so it floats above the
 * modal and is never clipped. Visually identical to the form's other inputs.
 */
export default function ActecoSelect({
  id,
  label,
  required,
  options,
  loading,
  value,
  onChange,
  error,
}) {
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const blurTimer = useRef(null);
  const [inputValue, setInputValue] = useState(labelOf(value));
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [visibleCount, setVisibleCount] = useState(PAGE);
  const [rect, setRect] = useState(null);

  // Keep the input text in sync when the selected value changes externally.
  useEffect(() => {
    setInputValue(labelOf(value));
  }, [value]);

  const query = inputValue.trim().toLowerCase();
  // Matching against code + activity. When the input still shows the selected
  // label, show the full catalog (so reopening lists everything).
  const filtered = useMemo(() => {
    if (!query || query === labelOf(value).toLowerCase()) return options;
    return options.filter((o) =>
      `${o.code} ${o.activity}`.toLowerCase().includes(query),
    );
  }, [options, query, value]);

  useEffect(() => {
    setVisibleCount(PAGE);
    setHighlight(0);
  }, [open, filtered.length]);

  const openList = () => {
    if (fieldRef.current) {
      const r = fieldRef.current.getBoundingClientRect();
      setRect({ top: r.bottom, left: r.left, width: r.width });
    }
    setOpen(true);
  };

  const select = (opt) => {
    onChange(opt);
    setInputValue(labelOf(opt));
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return openList();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered[highlight]) {
        e.preventDefault();
        select(filtered[highlight]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setVisibleCount((c) => (c < filtered.length ? c + PAGE : c));
    }
  };

  const listboxId = `${id}-listbox`;
  const activeId =
    open && filtered[highlight]
      ? `${id}-opt-${filtered[highlight].code}`
      : undefined;
  const showList = open && filtered.length > 0 && rect;

  return (
    <div className="acteco-field" ref={fieldRef}>
      <label className="acteco-label" htmlFor={id}>
        {label}
        {required ? ' *' : ''}
      </label>
      <input
        id={id}
        ref={inputRef}
        className={error ? 'form-input mono error' : 'form-input mono'}
        role="combobox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={activeId}
        autoComplete="off"
        placeholder={
          loading ? 'Cargando catálogo…' : 'Busca tu actividad o código…'
        }
        disabled={loading}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          openList();
        }}
        onFocus={openList}
        onKeyDown={onKeyDown}
        onBlur={() => {
          blurTimer.current = setTimeout(() => {
            setOpen(false);
            // Catalog-only: revert the text to the current selection.
            setInputValue(labelOf(value));
          }, 120);
        }}
      />
      {error && <span className="acteco-error">{error}</span>}
      {showList &&
        ReactDOM.createPortal(
          <ul
            id={listboxId}
            role="listbox"
            className="acteco-listbox"
            style={{
              position: 'fixed',
              top: rect.top,
              left: rect.left,
              width: rect.width,
              // Above an open modal (this select is used inside edit modals),
              // below toasts. Was a hardcoded 2000 (toast level).
              zIndex: 'var(--z-modal-popover, 1700)',
            }}
            onScroll={handleScroll}
            // Keep input focus when clicking an option (blur fires otherwise).
            onMouseDown={(e) => e.preventDefault()}
          >
            {filtered.slice(0, visibleCount).map((option, index) => (
              <li
                key={option.code}
                id={`${id}-opt-${option.code}`}
                role="option"
                aria-selected={index === highlight}
                className={`acteco-option${
                  index === highlight ? ' acteco-option--active' : ''
                }`}
                onMouseEnter={() => setHighlight(index)}
                onClick={() => {
                  clearTimeout(blurTimer.current);
                  select(option);
                  inputRef.current?.blur();
                }}
              >
                <span className="acteco-option__code">{option.code}</span>
                <span className="acteco-option__activity">
                  {option.activity}
                </span>
              </li>
            ))}
          </ul>,
          document.body,
        )}
    </div>
  );
}

ActecoSelect.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  required: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      activity: PropTypes.string.isRequired,
    }),
  ).isRequired,
  loading: PropTypes.bool,
  value: PropTypes.shape({
    code: PropTypes.string,
    activity: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};
